import * as THREE from 'three';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';

export function cloneGlbPreview(object){
  const clone=object.clone(true);
  clone.traverse((child)=>{
    if(!child.isMesh)return;
    child.geometry=child.geometry?.clone();
    child.material=Array.isArray(child.material)
      ?child.material.map((material)=>material.clone())
      :child.material?.clone();
  });
  return clone;
}

function sceneStats(object){
  let meshes=0;
  let triangles=0;
  object.traverse((child)=>{
    if(!child.isMesh||!child.geometry)return;
    meshes+=1;
    triangles+=child.geometry.index
      ?child.geometry.index.count/3
      :(child.geometry.attributes.position?.count||0)/3;
  });
  return {meshes,triangles:Math.round(triangles)};
}

export function formatGlbTriangles(value){
  return value>=1000?`${(value/1000).toFixed(1)}k tris`:`${value} tris`;
}

function normalizeCannon(source,box,size){
  const scale=1.15/Math.max(size.x,size.y,size.z);
  const center=box.getCenter(new THREE.Vector3());
  source.scale.setScalar(scale);
  source.position.set(-center.x*scale,-box.min.y*scale,-box.max.z*scale);
  const normalizedSize={
    x:size.x*scale,
    y:size.y*scale,
    z:size.z*scale,
  };
  const orientationWarning=normalizedSize.z<Math.max(normalizedSize.x,normalizedSize.y)*.72;
  return {
    scale,
    normalizedSize,
    anchor:'center-x / floor-y / mount-max-z',
    warnings:[
      orientationWarning?'El modelo parece más ancho o alto que largo; verificá que dispare hacia −Z.':'',
    ].filter(Boolean),
    orientationWarning,
  };
}

function normalizeTombstone(source,box,size){
  const scale=.92/size.y;
  const center=box.getCenter(new THREE.Vector3());
  source.scale.setScalar(scale);
  source.position.set(-center.x*scale,-box.min.y*scale,-center.z*scale);
  const normalizedSize={
    x:size.x*scale,
    y:size.y*scale,
    z:size.z*scale,
  };
  const proportionWarning=Math.max(normalizedSize.x,normalizedSize.z)>normalizedSize.y*1.65;
  return {
    scale,
    normalizedSize,
    anchor:'center-xz / floor-y',
    warnings:[
      proportionWarning?'El modelo es muy ancho respecto de su altura; revisá la orientación o la escala.':'',
    ].filter(Boolean),
    proportionWarning,
  };
}

function normalizeProjectile(source,box,size){
  const scale=.72/Math.max(size.x,size.y,size.z);
  const center=box.getCenter(new THREE.Vector3());
  source.scale.setScalar(scale);
  source.position.set(-center.x*scale,-center.y*scale,-center.z*scale);
  const normalizedSize={x:size.x*scale,y:size.y*scale,z:size.z*scale};
  const orientationWarning=normalizedSize.z<Math.max(normalizedSize.x,normalizedSize.y);
  return {
    scale,
    normalizedSize,
    anchor:'center-xyz / forward-minus-z',
    warnings:[orientationWarning?'El proyectil no parece tener su eje más largo sobre Z; revisá que apunte hacia −Z.':''].filter(Boolean),
    orientationWarning,
  };
}

export async function loadCatalogGlb(file,kind){
  if(!file?.name?.toLocaleLowerCase().endsWith('.glb')){
    throw new Error('Elegí un archivo con extensión .glb.');
  }
  const loader=new GLTFLoader();
  loader.setMeshoptDecoder(MeshoptDecoder);
  const dracoLoader=new DRACOLoader();
  dracoLoader.setDecoderPath(`${import.meta.env.BASE_URL}draco/`);
  loader.setDRACOLoader(dracoLoader);
  const gltf=await loader.parseAsync(await file.arrayBuffer(),'');
  const source=gltf.scene;
  const stats=sceneStats(source);
  if(!stats.meshes)throw new Error('El GLB no contiene mallas visibles.');
  const box=new THREE.Box3().setFromObject(source);
  const size=box.getSize(new THREE.Vector3());
  if(!Number.isFinite(size.length())||size.length()===0||size.y<=0){
    throw new Error('El GLB no contiene geometría visible con dimensiones válidas.');
  }
  const content=new THREE.Group();
  content.name=`normalized_${kind}_content`;
  content.add(source);
  const normalization=kind==='cannon'
    ?normalizeCannon(content,box,size)
    :kind==='projectile'
      ?normalizeProjectile(content,box,size)
      :normalizeTombstone(content,box,size);
  const complexityWarning=stats.triangles>80000;
  if(complexityWarning){
    normalization.warnings.push('El archivo supera las 80k tris y puede ser pesado para un cosmético.');
  }
  source.traverse((child)=>{
    if(!child.isMesh)return;
    child.castShadow=true;
    child.receiveShadow=true;
  });
  const normalized=new THREE.Group();
  normalized.name=`imported_${kind}_preview`;
  normalized.add(content);
  return {
    preview:normalized,
    stats:{
      ...stats,
      originalSize:{x:size.x,y:size.y,z:size.z},
      ...normalization,
      warnings:normalization.warnings,
      complexityWarning,
    },
  };
}
